/// <reference types="gtag.js" />

import type {
  ConsentSignals,
  ConsentValue,
} from '@/lib/consent/consent-signals';

import { readConsentCookie } from '@/lib/consent/consent-cookie';
import {
  DENY_ALL_SIGNALS,
  signalsFromChoices,
} from '@/lib/consent/consent-signals';

import { getOrCreateGtag } from './data-layer';

/**
 * Consent Mode's single writer.
 *
 * Every Google tag on the page resolves consent from one piece of page
 * state: the `gtag('consent', …)` calls on the dataLayer. This module is the
 * only thing that writes it — a second writer could publish a different
 * answer for the same signal, and which one a tag saw would come down to
 * load order.
 *
 * ## What a missing default does, in both directions
 *
 * Absent consent state is not one failure but two opposite ones, and a
 * reader who assumes either will be bitten by the other:
 *
 * - The six Meta/Reddit pixel tags are custom HTML using GTM's Additional
 *   Consent Checks (`consentStatus: needed` on `ad_storage` and
 *   `ad_user_data`). Those **fail closed**: with no default published, the
 *   pixels never fire, permanently and silently.
 * - The GA4 and Google Ads tags in the same container use built-in Consent
 *   Mode, which **fails open**: per Google's docs an unset consent value is
 *   not `denied`, so a tag that evaluates before a default is set behaves as
 *   if it were granted.
 *
 * So there is no safe direction to be late in. Publishing the default is
 * both what lets the ad pixels ever fire and what stops Google's own tags
 * firing on a visitor who never consented.
 *
 * ## Why it isn't an adapter
 *
 * Both `GOOGLE_ANALYTICS_MEASUREMENT_ID` and
 * `GOOGLE_TAG_MANAGER_CONTAINER_ID` are empty-string kill switches, so an
 * adapter that owned this would take Consent Mode down with it the moment
 * its secret is unset. Retiring the app's own GA4 stream in favour of the
 * container's is exactly that: GA unconfigured, GTM configured. So the
 * writer runs unconditionally in the browser, whether or not GA4 is
 * configured, and each thing that puts a Google script on the page —
 * `bootstrapGtag`, `loadGtmContainer` — calls {@link publishConsentDefault}
 * itself rather than trusting a sibling to have done it. It is idempotent,
 * so there is no order for those callers to get wrong.
 *
 * ## The two halves
 *
 * The default answers what the shared `rio_consent` cookie knows, which is
 * every decision recorded on any `.respondent.io` surface. The update
 * answers what the cookie cannot: a legacy record, the signed-in profile
 * copy, the region policy, or a decision made during this pageview. The
 * `wait_for_update: 500` window the default opens is what gives that
 * resolved answer time to land before tags act on the seeded one — which
 * is why seeding conservatively costs nothing.
 */

/**
 * Module-scoped guard so the default is queued once per page however many
 * times an analytics client is built (Strict Mode's double mount, dev
 * hot-reload, a future call site that bypasses the client cache).
 */
let defaultPublished = false;

/**
 * Queues the consent default. Returns whether the page has Consent Mode
 * state — false only during SSR, where there is no dataLayer to queue onto.
 */
export function publishConsentDefault(): boolean {
  if (globalThis.document === undefined) return false;
  if (defaultPublished) return true;

  getOrCreateGtag()('consent', 'default', buildConsentDefault());
  defaultPublished = true;
  return true;
}

/**
 * Publishes the resolved signal set. The **full** set every time: a partial
 * payload would leave a previously granted signal stale after a withdrawal.
 *
 * Publishes the default first when nothing has yet, so an update can never
 * be the first consent state on the page: Google's contract is that the
 * default precedes every update, and the ordering that guarantees it here
 * (default from `buildAdapters`, update from `AnalyticsClient.setConsent`)
 * is a property of two separate call sites rather than of this module.
 */
export function publishConsentUpdate(signals: ConsentSignals) {
  if (!publishConsentDefault()) return;
  getOrCreateGtag()('consent', 'update', toGtagConsentParams(signals));
}

/**
 * The analytics-only shorthand behind `AnalyticsClient.optIn` / `optOut`.
 * Deliberately narrow: it touches `analytics_storage` and nothing else, so
 * a caller that only means "analytics on" cannot accidentally grant ad
 * consent. Consent-bearing call sites use {@link publishConsentUpdate}.
 */
export function publishAnalyticsConsent(value: ConsentValue) {
  if (!publishConsentDefault()) return;
  getOrCreateGtag()('consent', 'update', { analytics_storage: value });
}

/**
 * Builds the `gtag('consent', 'default', …)` payload.
 *
 * The default is read from `rio_consent` rather than being hardcoded denied:
 * a visitor who already decided here should not have their first pageview
 * fire under a denied default and then get silently upgraded. When there is
 * no cookie the result is deny-everything, which is the required default.
 *
 * **This must stay a read.** `readConsentCookie` never writes. This runs
 * while `AnalyticsProvider` — a child of `ConsentProvider` — first renders,
 * so it happens before the provider's effect. Writing here would record a
 * decision the visitor never made.
 *
 * `unset` collapses to `denied` in `signalsFromChoices` — gtag has no third
 * state, and this module is the boundary that collapse is allowed at.
 *
 * Under denied state, GA4 supports two property-side behaviors:
 *  - Basic mode: tags don't fire at all
 *  - Advanced mode (cookieless pings): tags fire without identifiers, and
 *    GA's backend uses the pings for behavioral modeling
 * Which one happens is controlled in the GA4 admin (Consent Signals +
 * Behavioral Modeling toggles). The code is the same either way — we
 * just communicate consent state honestly and let GA decide.
 */
function buildConsentDefault(): Gtag.ConsentParams {
  return { ...toGtagConsentParams(readSeedSignals()), wait_for_update: 500 };
}

/**
 * The stored decision, or deny-all when there isn't one — and also when
 * reading it fails. Publishing something is not optional: the pixel tags
 * fail closed on absent state but Google's own tags fail open, so an
 * unreadable cookie must not be allowed to become no consent state at all.
 * `setConsent` still corrects a conservative seed inside the
 * `wait_for_update` window.
 */
function readSeedSignals(): ConsentSignals {
  try {
    const stored = readConsentCookie();
    return stored ? signalsFromChoices(stored.choices) : DENY_ALL_SIGNALS;
  } catch (error) {
    console.error('Consent default seed error', error);
    return DENY_ALL_SIGNALS;
  }
}

/**
 * Maps our signal set to the gtag consent payload. `security_storage` and
 * `functionality_storage` / `personalization_storage` are included because
 * Consent Mode understands them and other tags on the page (via GTM) may key
 * off them — GA4 itself only reads the analytics and ad signals.
 */
function toGtagConsentParams(signals: ConsentSignals): Gtag.ConsentParams {
  return {
    security_storage: signals.security_storage,
    functionality_storage: signals.functionality_storage,
    personalization_storage: signals.personalization_storage,
    analytics_storage: signals.analytics_storage,
    ad_storage: signals.ad_storage,
    ad_user_data: signals.ad_user_data,
    ad_personalization: signals.ad_personalization,
  };
}

/**
 * Test-only reset hook — clears the published-default guard AND the globals
 * the bootstrap owns (`gtag`, `dataLayer`), so each test starts from a clean
 * page without callers having to remember to wipe them separately.
 */
export function __resetConsentModeBootstrapForTesting() {
  defaultPublished = false;
  Reflect.deleteProperty(globalThis, 'gtag');
  globalThis.dataLayer = undefined;
}
